import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useContent } from "../../hooks/useContent";
import { useAuth } from "../../hooks/useAuth";
import { get_companies_api } from "../../DAL/CompanyList";
import { get_support_tickets_api } from "../../DAL/SupportTickets";
import moment from "moment";

const Home = () => {
  const navigate = useNavigate();
  const { setPageHeading } = useContent();
  const { userInfo } = useAuth();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalTickets: 0,
    openTickets: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const quickActions = [
    {
      title: "Create Hospital or Clinic",
      icon: "mdi:office-building",
      link: "/create-company",
      color: "#023a7a",
      gradient: "linear-gradient(135deg, #023a7a 0%, #034a9a 100%)",
    },
    {
      title: "Manage Hospitals or Clinics",
      icon: "mdi:account-edit",
      link: "/manage-companies",
      color: "#2563eb",
      gradient: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
    },
    {
      title: "Support Tickets",
      description: "Create and manage tickets",
      icon: "mdi:ticket-confirmation",
      link: "/support-tickets",
      color: "#7c3aed",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
    },
    {
      title: "Email Support",
      description: "Send support request via email",
      icon: "mdi:email-fast",
      link: "/email-support",
      color: "#db2777",
      gradient: "linear-gradient(135deg, #db2777 0%, #ec4899 100%)",
    },
    {
      title: "Web Conference",
      description: "Schedule or join meetings",
      icon: "mdi:video-box",
      link: "/meetings",
      color: "#0891b2",
      gradient: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
    },
    {
      title: "Partner Reference",
      description: "Access documentation",
      icon: "mdi:book-open-page-variant",
      link: "http://partnerreference.myveekrypt.com",
      color: "#059669",
      gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    },
  ];

  const statCards = [
    {
      title: "Total Companies",
      value: stats.totalCompanies,
      icon: "mdi:office-building",
      color: "#023a7a",
      bgColor: "rgba(2, 58, 122, 0.1)",
      link: "/manage-companies",
    },
    {
      title: "Active Licenses",
      value: stats.activeCompanies,
      icon: "mdi:check-circle",
      color: "#059669",
      bgColor: "rgba(5, 150, 105, 0.1)",
      link: "/manage-companies",
    },
    {
      title: "Support Tickets",
      value: stats.totalTickets,
      icon: "mdi:ticket",
      color: "#7c3aed",
      bgColor: "rgba(124, 58, 237, 0.1)",
      link: "/support-tickets",
    },
    {
      title: "Open Tickets",
      value: stats.openTickets,
      icon: "mdi:alert-circle",
      color: "#dc2626",
      bgColor: "rgba(220, 38, 38, 0.1)",
      link: "/support-tickets",
    },
  ];

  useEffect(() => {
    setPageHeading("Partner Portal Dashboard");
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      // Load companies
      const companiesResult = await get_companies_api({
        search: "",
        sort: "company_name",
      });
      let totalCompanies = 0;
      let activeCompanies = 0;
      if (companiesResult.code === 200) {
        totalCompanies = companiesResult.companies?.length || 0;
        activeCompanies =
          companiesResult.companies?.filter((c) => c.status === "Active")
            ?.length || 0;
      }

      // Load tickets
      const ticketsResult = await get_support_tickets_api({
        search: "",
        sort: "status",
      });
      let totalTickets = 0;
      let openTickets = 0;
      if (ticketsResult.code === 200) {
        totalTickets = ticketsResult.tickets?.length || 0;
        openTickets =
          ticketsResult.tickets?.filter(
            (t) => t.status === "Open" || t.status === "In Progress"
          )?.length || 0;
      }

      setStats({
        totalCompanies,
        activeCompanies,
        totalTickets,
        openTickets,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
    setIsLoadingStats(false);
  };

  const handleClick = (link) => {
    if (link.startsWith("http")) {
      window.open(link, "_blank", "noopener noreferrer");
    } else {
      navigate(link);
    }
  };

  const getGreeting = () => {
    const hour = moment().hour();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <div className="Home">
      <div className="container">
        {/* Welcome Section */}
        <div className="welcome-section mb-4">
          <div className="welcome-card">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div>
                <h3 className="mb-2">
                  {getGreeting()},{" "}
                  <span className="fw-bold">
                    {userInfo?.first_name || "Partner"}
                  </span>
                  !
                </h3>
                <p className="text-muted mb-0">
                  Welcome to your Partner Portal Dashboard. Manage your company
                  accounts and support tickets efficiently.
                </p>
              </div>
              <div className="welcome-icon">
                <Icon icon="mdi:hand-wave" width="48" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-section mb-4">
          <div className="row g-3">
            {statCards.map((stat, index) => (
              <div key={index} className="col-12 col-sm-6 col-lg-3">
                <div
                  className="stat-card"
                  onClick={() => handleClick(stat.link)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="stat-icon" style={{ color: stat.color }}>
                    <div
                      className="icon-wrapper"
                      style={{ backgroundColor: stat.bgColor }}
                    >
                      <Icon icon={stat.icon} width="32" />
                    </div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value" style={{ color: stat.color }}>
                      {isLoadingStats ? (
                        <Icon icon="mdi:loading" className="spin-animation" />
                      ) : (
                        stat.value
                      )}
                    </div>
                    <div className="stat-title">{stat.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="quick-actions-section mb-4">
          <div className="section-header mb-3">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <Icon icon="mdi:lightning-bolt" width="24" />
              Quick Actions
            </h5>
          </div>
          <div className="row g-3">
            {quickActions.map((action, index) => (
              <div key={index} className="col-12 col-md-6 col-lg-4">
                <div
                  className="quick-action-card"
                  onClick={() => handleClick(action.link)}
                >
                  <div
                    className="action-icon-wrapper"
                    style={{ background: action.gradient }}
                  >
                    <Icon icon={action.icon} width="28" color="white" />
                  </div>
                  <div className="action-content">
                    <h6 className="action-title">{action.title}</h6>
                    <p className="action-description">{action.description}</p>
                  </div>
                  <div className="action-arrow">
                    <Icon
                      icon="mdi:arrow-right"
                      width="20"
                      style={{ color: action.color }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div className="help-section">
          <div className="help-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="help-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                }}
              >
                <Icon icon="mdi:help-circle" width="32" color="white" />
              </div>
              <div className="flex-fill">
                <h6 className="mb-1">Need Help?</h6>
                <p className="text-muted mb-0 small">
                  Contact our support team or check the partner reference guide
                  for assistance.
                </p>
              </div>
              <button
                className="theme-button"
                onClick={() => handleClick("/email-support")}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
